// lazy-screen.js - turn a screen module into a lazily-loaded one.
//
// Most screens (director selection, dashboard, rankings, the player and coach
// views, the PDF generators) are only reached through user actions, and a
// parent or player never opens the director ones at all. Eagerly importing them
// all put every screen in the main bundle. lazyScreen() wraps a module so its
// code is dynamic-imported the first time one of its methods is called, then
// cached. Vite gives each wrapped module its own chunk, so the main bundle only
// carries the shell plus whatever the current user actually opens.
//
// The wrapper is a Proxy: every property access returns a function that loads
// the module (once) and calls the real method. Call sites therefore stay
// exactly as they were - `SELECTION.showList()` still reads `SELECTION.showList()`.
//
// IMPORTANT: only for modules invoked as fire-and-forget method calls. A module
// whose return value is read synchronously (e.g. CALIBRATION.getEffectiveCeiling,
// BASELINE_TESTER.exitToHubIfStaff) must stay a normal eager import, because
// every method here returns a promise.
//
// Error handling is scoped to the LOAD only: if the chunk cannot be fetched
// we recover or surface one toast, and let the next attempt retry. A method's
// own errors propagate untouched, so existing per-call try/catch keeps working.
//
// WHY A FAILED LOAD USUALLY MEANS "STALE APP", NOT "NO INTERNET":
// every deploy gives each chunk a new content-hashed filename, and the old
// files stop being served. The app is installed as a PWA, so a device can sit
// on a cached copy of index.html that asks for filenames which no longer
// exist. The result is a 404 per screen, and the user is told to check a
// connection that is working perfectly. It happened to the co-director: every
// screen failed except the squad grid, whose hash happened not to have changed.
//
// So when the device is online, a load failure is treated as a stale build and
// repaired: drop the caches, drop the service worker, reload once. Guarded by a
// session flag so a genuinely broken chunk cannot put the app in a reload loop,
// and the flag is cleared by any successful load.

import { UI } from './ui.js';

const RELOAD_GUARD = 'khc_stale_build_reload';

function _guardTripped() {
  try { return sessionStorage.getItem(RELOAD_GUARD) === '1'; } catch { return true; }
}

/**
 * Remember that we already tried to repair this session.
 * Returns false when the flag could NOT be stored, and the caller must then
 * not reload: the flag has to outlive the reload to stop the next failure
 * triggering another one. A browser with sessionStorage blocked (private mode,
 * locked-down settings) would otherwise refresh forever, which is a far worse
 * bug than the stale screen this is repairing.
 */
function _setGuard(on) {
  try {
    if (on) sessionStorage.setItem(RELOAD_GUARD, '1');
    else sessionStorage.removeItem(RELOAD_GUARD);
    return true;
  } catch {
    return false;
  }
}

/** Throw away this device's cached copy of the app and load it fresh. */
async function _reloadFresh() {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations?.()) || [];
    await Promise.all(regs.map(r => r.unregister()));
  } catch { /* best effort */ }
  try {
    const keys = (await window.caches?.keys?.()) || [];
    await Promise.all(keys.map(k => caches.delete(k)));
  } catch { /* best effort */ }
  window.location.reload();
}

/**
 * @param {() => Promise<object>} loader  e.g. () => import('./selection.js')
 * @param {string} exportName             the named export, e.g. 'SELECTION'
 */
export function lazyScreen(loader, exportName) {
  let modPromise = null;

  const load = () => {
    if (!modPromise) {
      modPromise = loader()
        .then(mod => {
          // A screen loaded, so this build is coherent and any earlier repair
          // worked. Re-arm the guard for a future deploy in the same session.
          _setGuard(false);
          return mod[exportName];
        })
        .catch(err => {
          modPromise = null; // let a later click try again
          console.error(`Lazy load of ${exportName} failed:`, err);
          const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
          // Only repair if the "already tried" flag actually stuck. See _setGuard.
          if (!offline && !_guardTripped() && _setGuard(true)) {
            UI.toast('Updating to the latest version...', 'info', 1500);
            _reloadFresh();
          } else {
            UI.toast(offline
              ? 'You appear to be offline. Reconnect and try again.'
              : 'Could not open this screen. Please close the app fully and reopen it.', 'error');
          }
          throw err;
        });
    }
    return modPromise;
  };

  return new Proxy({}, {
    get(_target, prop) {
      return (...args) => load().then(mod => mod[prop](...args));
    },
  });
}
